import { useEffect, useState } from 'react'
import { Platform } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { i18n } from '$localization/config.js'
// components
import {
  showDefaultAlert,
  showGenericServerError,
  handleDefaultError,
} from '$navigation/_utils/NavigationUtils.js'
import DefaultNavigationOptions from '$navigation/_components/DefaultNavigationOptions.js'
import { DefaultFullHeight } from '$constants/styles/global.styles.js'
import { WEB } from '$constants/Platforms.js'
import ApiFetchHandler from '$api/ApiFetchHandler.js'
import { getLang } from '$utils/lang_utils.js'

// actions
import { setShouldReloadGeneticTestings } from '$redux/member_profile/actions.js'
import { setError } from '$redux/defaults/actions.js'
// COMPONENTS
import GeneTestUI from './GeneTestUI'
import { checkForMoreButtons } from './utils/addingMoreAndUnsureButtonMethod'

import { RAW_LAST_VISITED_SCREENS } from '$navigation/constants/lastVisitedScreensRoutes'

import { CUSTOM_FLOW_ROUTES } from '../../../navigation/constants/routes'
import { setLastLoginScreen } from '../../../redux/defaults/actions'

let hasGenderIdentity = false
let isLastScreenBeforeLogin = false

export default function PositiveGeneTestMain({ navigation }) {
  const geneTestType = 'p'
  const loadMainQuestion = false

  const currentMember = useSelector(
    (state) => state.memberProfileStore.currentMember
  )
  const colorSettings = useSelector((state) => state.store.colorSettings)
  const geneTestFromOnboarding = useSelector(
    (state) => state.geneTestingStore.fromOnboarding
  )
  const authCredentials = useSelector(
    (state) => state.accountStore.authCredentials
  )
  const probandProfile = useSelector(
    (state) => state.probandStore.probandProfile
  )
  const lastScreenLogin = useSelector((state) => state.store.lastScreenLogin)
  const currentSelectedMember = useSelector(
    (state) => state.memberProfileStore.currentMember
  )
  const selectedLanguage = useSelector((state) => state.store.selectedLanguage)

  const dispatch = useDispatch()
  const saveLatestScreen = (data) => dispatch(setLastLoginScreen(data))
  const setShouldReloadGeneticTesting = (data) =>
    dispatch(setShouldReloadGeneticTestings(data))
  const saveError = (data) => dispatch(setError(data))

  const [startCount, setStartCount] = useState(0)
  const [mainQuestionValue, setMainQuestionValue] = useState(true)
  const [loadSubQuestion, setLoadSubQuestion] = useState(true)
  const [subQuestionValue, setSubQuestionValue] = useState(null)
  const [loadGeneList, setLoadGeneList] = useState(false)
  const [listValues, setListValues] = useState([])
  const [disableDoneButton, setDisableDoneButton] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [scrollViewHeight, setScrollViewHeight] = useState(DefaultFullHeight())
  const [selectedGeneTest, setSelectedGeneTest] = useState([])
  const [onLoadSelectedGenes, setOnLoadSelectedGenes] = useState([])
  const [UMLSGenesList, setUMLSGenesList] = useState([])

  const screenResizeHandler = () => {
    const height = DefaultFullHeight()
    setScrollViewHeight(height)
  }

  const loadGeneTest = async () => {
    const getGeneTestingListPayload = {
      path: `genetic_testing/?lang=${getLang().toString()}`,
      method: 'get',
      pageDetails: {
        page: 'PositiveGeneTestMain.js',
      },
    }

    const response = await ApiFetchHandler(getGeneTestingListPayload)

    if (response.isError) {
      saveError({
        isShown: true,
        status: response.status,
        message: response.error,
      })
      return
    }

    setListValues(response)
  }

  const loadUMLSGenes = async (gene, moreButton) => {
    const isMoreButtonSelected = moreButton === 'more'
    const startedCount = isMoreButtonSelected ? startCount + 10 : 0

    const getUmlsPayload = {
      path: `umls/gene/?term=${gene}&start=${startedCount}`,
      method: 'get',
      token: authCredentials.accessToken,
      pageDetails: {
        page: 'PositiveGeneTestMain.js',
      },
    }

    const response = await ApiFetchHandler(getUmlsPayload)

    if (response.isError) {
      saveError({
        isShown: true,
        status: response.status,
        message: response.error,
      })
      return
    }

    const UMLSGenes = response.ConceptList

    if (UMLSGenes.length > 0) {
      const result = checkForMoreButtons(UMLSGenes)
    }

    const filteredGenes = UMLSGenes.map((item) => {
      const isSelectedGenes = selectedGeneTest.some(
        (selectedGene) => selectedGene.CUI === item.CUI
      )
      return { ...item, isSelected: isSelectedGenes }
    })

    setUMLSGenesList(
      isMoreButtonSelected
        ? (prevState) => [...prevState.slice(0, -2), ...filteredGenes]
        : filteredGenes
    )

    setStartCount(startedCount)
  }

  const handleMainQuestion = (value) => {
    setSubQuestionValue(null)
    setLoadGeneList(false)
    setSelectedGeneTest([])
    switch (value) {
      case true:
        setMainQuestionValue(value)
        setLoadSubQuestion(true)
        setDisableDoneButton(true)
        break
      case false:
        setMainQuestionValue(value)
        setLoadSubQuestion(false)
        setDisableDoneButton(false)
        break
      default:
        setDisableDoneButton(true)
    }
  }

  const handleSubQuestion = (value) => {
    setDisableDoneButton(false)
    setSelectedGeneTest([])
    switch (value) {
      case true:
        setSubQuestionValue(value)
        setLoadGeneList(true)
        break
      case false:
        setSubQuestionValue(value)
        setLoadGeneList(false)
        break
      default:
        setDisableDoneButton(true)
    }
  }

  const handleDeleteSelectedGene = (itemSelected) => {
    const filteredSelectedGeneTest = selectedGeneTest.filter(
      (gene) => gene.CUI !== itemSelected.CUI
    )
    const selectedGenesList = UMLSGenesList.map((item) => ({
      ...item,
      isSelected: item.CUI === itemSelected.CUI ? false : item.isSelected,
    }))
    setUMLSGenesList(selectedGenesList)
    setSelectedGeneTest(filteredSelectedGeneTest)
  }

  const handleSelectedGeneTest = (itemSelected) => {
    const isGeneSelected = itemSelected.isSelected
    const selectedGenesList = UMLSGenesList.map((item) => ({
      ...item,
      isSelected:
        item.CUI === itemSelected.CUI ? !isGeneSelected : item.isSelected,
    }))
    setUMLSGenesList(selectedGenesList)
    setSelectedGeneTest((prevState) => [...prevState, itemSelected])
    isGeneSelected && handleDeleteSelectedGene(itemSelected)
  }

  const loadMemberGeneTests = async () => {
    setIsLoading(true)

    const getMemberProfilePayload = {
      path: 'member/get/',
      method: 'post',
      token: authCredentials.accessToken,
      body: {
        proband_id: probandProfile.memberID,
        member_id: currentSelectedMember.member_id,
        lang: selectedLanguage,
      },
      pageDetails: {
        page: 'PositiveGeneTestMain.js',
      },
    }

    const memberGeneTest = await ApiFetchHandler(getMemberProfilePayload)

    if (memberGeneTest.isError) {
      saveError({
        isShown: true,
        status: memberGeneTest.status,
        message: memberGeneTest.error,
      })
      return
    }

    const filteredMemberGeneList = memberGeneTest.member_genetic_testing.filter(
      (geneTest) => geneTest.result === geneTestType
    )

    if (filteredMemberGeneList.length === 0) return handleSubQuestion(null)

    handleSubQuestion(true)

    const loadedGeneTest = []

    filteredMemberGeneList.forEach((item) => {
      const payload = {
        CUI: item.umls_id,
        Name: item.umls_name,
        member_gene_id: item.id,
      }

      loadedGeneTest.push(payload)
      handleSelectedGeneTest(payload)
    })

    setOnLoadSelectedGenes(loadedGeneTest)
  }

  const componentMountHandler = async () => {
    try {
      if (WEB === Platform.OS) {
        window.addEventListener('resize', screenResizeHandler)
      }

      if (probandProfile && probandProfile.profileData.genderIdentity) {
        hasGenderIdentity = true
      } else {
        hasGenderIdentity = false
      }

      isLastScreenBeforeLogin =
        lastScreenLogin === RAW_LAST_VISITED_SCREENS.gene_testing

      saveLatestScreen(CUSTOM_FLOW_ROUTES?.PersonalGeneticTesting.name)
      navigation.setParams({
        cs: colorSettings,
      })

      setIsLoading(true)
      setShouldReloadGeneticTesting(true)
      await loadGeneTest()
      await loadMemberGeneTests()
      setIsLoading(false)
    } catch (error) {
      console.log(
        '🚀 \n\n file: PositiveGeneTestMain.js:280 \n\n componentMountHandler \n\n error:',
        error
      )
    }
  }

  useEffect(() => {
    componentMountHandler()
  }, [])

  const handleGeneTestSaving = async () => {
    const savedGeneTestData = selectedGeneTest.filter(
      (testA) => !onLoadSelectedGenes.some((testB) => testA.CUI === testB.CUI)
    )
    const removedGeneTestData = onLoadSelectedGenes.filter(
      (testA) => !selectedGeneTest.some((testB) => testA.CUI === testB.CUI)
    )

    setIsLoading(true)

    await Promise.all(
      savedGeneTestData.map(async (item) => {
        const addGeneTestToMemberOnboardingPayload = {
          path: `member/${currentSelectedMember.member_id}/add_genetic_testing/`,
          method: 'post',
          token: authCredentials.accessToken,
          body: {
            proband_id: probandProfile.memberID,
            umls_id: item.CUI,
            umls_name: item.Name,
            result: geneTestType,
          },
          pageDetails: {
            page: 'PositiveGeneTestMain.js',
          },
        }

        const response = await ApiFetchHandler(
          addGeneTestToMemberOnboardingPayload
        )

        if (response.isError) {
          return saveError({
            isShown: true,
            status: response.status,
            message: response.error,
          })
        }
      })
    )
    // handle removing
    await Promise.all(
      removedGeneTestData.map(async (item) => {
        const removeMemberGeneTestingPayload = {
          path: `member/${currentSelectedMember.member_id}/remove_genetic_testing/`,
          method: 'post',
          token: authCredentials.accessToken,
          body: {
            proband_id: probandProfile.memberID,
            member_genetic_testing_id: item.member_gene_id,
          },
          pageDetails: {
            page: 'PositiveGeneTestMain.js',
          },
        }

        const response = await ApiFetchHandler(removeMemberGeneTestingPayload)

        if (response.isError) {
          return saveError({
            isShown: true,
            status: response.status,
            message: response.error,
          })
        }
      })
    )
    setIsLoading(false)
    // navigation.replace('EditProfileView')
    navigation.pop()
  }

  const handleToolTip = () => {
    showDefaultAlert(
      i18n.t('test_positive').default,
      i18n.t(
        'testing_positive_means_that_a_change_or_mutation_was_found_in_the_gene_this_could_also_be_worded_as_pathogenic_likely_pathogenic_deleterious_or_likely_deleterious_on_a_report'
      ).default
    )
  }

  return (
    <GeneTestUI
      // PROPS
      navigation={navigation}
      buttonText="done"
      // REDUX
      clickedMember={currentMember}
      currentMember={probandProfile}
      colorSettings={colorSettings}
      geneTestFromOnboarding={geneTestFromOnboarding}
      // STATE
      setUMLSGenesList={setUMLSGenesList}
      UMLSGenesList={UMLSGenesList}
      loadMainQuestion={loadMainQuestion}
      mainQuestionValue={mainQuestionValue}
      loadSubQuestion={loadSubQuestion}
      subQuestionValue={subQuestionValue}
      loadGeneList={loadGeneList}
      listValues={listValues}
      disableDoneButton={disableDoneButton}
      isLoading={isLoading}
      scrollViewHeight={scrollViewHeight}
      selectedGeneTest={selectedGeneTest}
      // METHODS
      handleDeleteSelectedGene={handleDeleteSelectedGene}
      loadUMLSGenes={loadUMLSGenes}
      handleMainQuestion={handleMainQuestion}
      handleSubQuestion={handleSubQuestion}
      handleSelectedGeneTest={handleSelectedGeneTest}
      handleGeneTestSaving={handleGeneTestSaving}
      handleToolTip={handleToolTip}
    />
  )
}
